<!--
    [text] is intentionally bound to aboveText() / belowText() (computed from the story input),
    NOT to aboveContent() / belowContent() (which track keystrokes locally).

    Froala's internal model is reset whenever the [froalaModel] binding receives a new value.
    If we passed the locally-tracked signal back as [text], every keystroke would re-push content
    into the editor, causing Froala to jump the cursor to position 0 mid-typing.

    Instead, [text] only changes when a new story is loaded (aboveText / belowText change),
    and typed content is captured separately via (contentChanged) → onAboveChanged / onBelowChanged.

    The toolbar lives here (outside both editors' DOM) and targets whichever editor was last focused.
    Since it is not inside Froala's .fr-box, clicking toolbar buttons never triggers Froala's
    internal fr-element.focus() call, so lastFocusedEditor is never corrupted by spurious focusin events.
-->
<st-story-toolbar
    [editor]="currentEditorInstance()"
    [disabled]="disabled()"
    [hasUnsavedChanges]="hasUnsavedChanges()"
    (saveClicked)="onSave()"
    (productionCommandClicked)="onToolbarProductionCommand($event)" />

@if (collaborationEnabled()) {
    <st-story-collaboration-text-editor
        #aboveCollabEditor
        class="st-story-editor__above"
        [class.top]="currentPosition() === BlackLinePosition.TOP"
        [class.middle]="currentPosition() === BlackLinePosition.MIDDLE"
        [class.bottom]="currentPosition() === BlackLinePosition.BOTTOM"
        [text]="aboveText()"
        [docId]="aboveDocId()"
        [disabled]="disabled()"
        [storyGuid]="story()?.Guid"
        [approved]="!!story()?.Approved"
        (collabUsers)="onCollabUsersChanged($event)"
        (codoxInitializing)="aboveCodoxInitializing.set($event)"
        (codoxFailed)="onAboveCodoxFailed()"
        (focusin)="updateLastFocusedEditor('above')"
        (selectedTextChange)="selectedTextChange.emit($event)"
        (contentChanged)="onAboveChanged($event)"
        (mosItemOpen)="mosItemOpen.emit($event)"
        (mosMediaClicked)="mosMediaClicked.emit($event)"
        (approvedChanged)="approvedChanged.emit($event)"
        (save)="onSave()" />

    <ng-container *ngTemplateOutlet="blackLine" />

    <st-story-collaboration-text-editor
        #belowCollabEditor
        class="st-story-editor__below"
        [class.bottom]="currentPosition() === BlackLinePosition.BOTTOM"
        [text]="belowText()"
        [docId]="belowDocId()"
        [disabled]="disabled()"
        [storyGuid]="story()?.Guid"
        [approved]="!!story()?.Approved"
        (collabUsers)="onCollabUsersChanged($event)"
        (codoxInitializing)="belowCodoxInitializing.set($event)"
        (focusin)="updateLastFocusedEditor('below')"
        (selectedTextChange)="selectedTextChange.emit($event)"
        (contentChanged)="onBelowChanged($event)"
        (mosItemOpen)="mosItemOpen.emit($event)"
        (mosMediaClicked)="mosMediaClicked.emit($event)"
        (approvedChanged)="approvedChanged.emit($event)"
        (save)="onSave()" />
} @else {
    <st-story-basic-text-editor
        #aboveBasicEditor
        class="st-story-editor__above"
        [class.top]="currentPosition() === BlackLinePosition.TOP"
        [class.middle]="currentPosition() === BlackLinePosition.MIDDLE"
        [class.bottom]="currentPosition() === BlackLinePosition.BOTTOM"
        [text]="aboveText()"
        [disabled]="disabled()"
        [storyGuid]="story()?.Guid"
        [approved]="!!story()?.Approved"
        (focusin)="updateLastFocusedEditor('above')"
        (selectedTextChange)="selectedTextChange.emit($event)"
        (contentChanged)="onAboveChanged($event)"
        (mosItemOpen)="mosItemOpen.emit($event)"
        (mosMediaClicked)="mosMediaClicked.emit($event)"
        (approvedChanged)="approvedChanged.emit($event)"
        (save)="onSave()" />

    <ng-container *ngTemplateOutlet="blackLine" />

    <st-story-basic-text-editor
        #belowBasicEditor
        class="st-story-editor__below"
        [class.bottom]="currentPosition() === BlackLinePosition.BOTTOM"
        [text]="belowText()"
        [disabled]="disabled()"
        [storyGuid]="story()?.Guid"
        [approved]="!!story()?.Approved"
        (focusin)="updateLastFocusedEditor('below')"
        (selectedTextChange)="selectedTextChange.emit($event)"
        (contentChanged)="onBelowChanged($event)"
        (mosItemOpen)="mosItemOpen.emit($event)"
        (mosMediaClicked)="mosMediaClicked.emit($event)"
        (approvedChanged)="approvedChanged.emit($event)"
        (save)="onSave()" />
}

<div
    class="st-story-editor__not-for-broadcast-watermark"
    [attr.data-content]="story()?.LegalBlock ? i18n().PSTEditorStoryNotToBeBroadcast : ''"></div>

<ng-template #blackLine>
    <div class="st-story-editor__black-line">
        <div class="st-story-editor__black-line__arrow-controls">
            <div class="st-story-editor__black-line__circle-wrapper">
                <button
                    class="st-story-editor__black-line__arrow-btn"
                    [class.st-story-editor__black-line__arrow-btn--colored]="currentPosition() === BlackLinePosition.BOTTOM && hasContentBelow()"
                    [class.st-story-editor__black-line__arrow-btn--hidden]="currentPosition() === BlackLinePosition.TOP"
                    (mousedown)="$event.preventDefault()"
                    (click)="onArrowUpClick()">
                    <mat-icon fontSet="material-symbols-outlined">keyboard_arrow_up</mat-icon>
                </button>
                <button
                    class="st-story-editor__black-line__arrow-btn"
                    [class.st-story-editor__black-line__arrow-btn--invisible]="currentPosition() === BlackLinePosition.BOTTOM"
                    (mousedown)="$event.preventDefault()"
                    (click)="onArrowDownClick()">
                    <mat-icon fontSet="material-symbols-outlined">keyboard_arrow_down</mat-icon>
                </button>
            </div>
        </div>
    </div>
</ng-template>
